import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Warning from "@mui/icons-material/Warning";
import {useTranslation} from "react-i18next";
import {
    Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    FloorplanService
} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {
    endOfDay,
    formatDateHMRange,
    formatFullDMY,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {
    FloorplanReservationItemDTO,
} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {FloorplanTable} from "../../types";
import {getOverlappingReservations} from "../../utils/drop-utils";
import {
    getIconForLanguage,
    getNameForLanguage,
} from "crqlar-react-common/dist/utils/translations.util";
import RestaurantReservationPreviewCard from "./RestaurantReservationPreviewCard";
import SaveWithEmailSplitButton from "./SaveWithEmailSplitButton";
import {getReservationDisplay} from "../../utils/reservation-utils";
import {useFloorplanContext} from "../../hooks/useFloorplanContext";

const getConflictReason = (conflicts: FloorplanReservationItemDTO[]): string => {
    return conflicts.map(c => {
        const profile = c.profiles?.[0];
        const name = profile
            ? [profile.lastName, profile.firstName].filter(Boolean).join(' ')
            : '';
        const res = c.restaurantReservation;
        const time = formatDateHMRange(res?.expectedStartDate, res?.expectedEndDate);
        return name ? `${name} (${time})` : time;
    }).join(', ');
};

const getEatPeriodLabel = (
    eatPeriod: unknown,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => {
    if (typeof eatPeriod !== 'string') return '';
    const value = eatPeriod.trim();
    if (!value) return '';
    return t(`Eat period ${value}`);
};

export interface DayUpdate {
    reservation: Reservation;
    newTableId: string;
}

interface DayRow {
    reservation: Reservation;
    date: string;
    startDate: Date;
    endDate: Date;
    currentTableName: string;
    targetTableId: string;
    targetTableName: string;
    hasConflict: boolean;
    conflicts: FloorplanReservationItemDTO[];
    assignedForDay: FloorplanReservationItemDTO[];
}

type Scope = 'single' | 'onwards' | 'all';

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (updates: DayUpdate[], sendEmail: boolean) => Promise<void> | void;
    reservation: Reservation;
    original: Reservation;
    linked: Reservation[];
    // Loaded separately by the parent so we always have a reliable stay start/end
    // for this guest, independent of what the reservationGroupId-linked list returns.
    accommodation?: Reservation;
    floorplanTables: FloorplanTable[];
    profiles?: Profile[];
    accommodationResources?: Resource[];
}

interface ConflictOptionContentProps {
    optionName: string;
    conflict?: FloorplanReservationItemDTO;
    conflictsCount: number;
}

const ConflictOptionContent: React.FC<ConflictOptionContentProps> = ({
                                                                         optionName,
                                                                         conflict,
                                                                         conflictsCount,
                                                                     }) => {
    const {
        guestNames,
        primaryLanguage,
        roomNames,
        timeRange,
    } = getReservationDisplay({
        reservation: conflict?.restaurantReservation,
        profiles: conflict?.profiles,
        accommodationResources: conflict?.accommodationResources,
    });
    const detail = [guestNames, roomNames, timeRange].filter(Boolean).join(' · ');

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            width: '100%'
        }}>
            <Typography variant="body2" fontWeight={500}
                        color={conflict ? 'error.main' : 'success.main'}
                        sx={{flex: '0 0 auto'}}>{optionName}</Typography>
            {conflict && <>
                {primaryLanguage && (
                    <Box
                        component="img"
                        alt={getNameForLanguage(primaryLanguage)}
                        src={getIconForLanguage(primaryLanguage)}
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flex: '0 0 auto'
                        }}
                    />
                )}
                <Typography variant="caption" color="error.main" noWrap
                            sx={{minWidth: 0}}>
                    {detail}
                    {conflictsCount > 1 ? ` +${conflictsCount - 1}` : ''}
                </Typography>
            </>}
        </Box>
    );
};

const GroupReservationConflictDialog: React.FC<Props> = ({
                                                             open,
                                                             onClose,
                                                             onConfirm,
                                                             reservation,
                                                             original,
                                                             linked,
                                                             accommodation,
                                                             floorplanTables,
                                                             profiles,
                                                             accommodationResources,
                                                         }) => {
    const {t} = useTranslation();
    const {restaurantId} = useFloorplanContext();
    const sourceTableId = (original.resourceIds ?? []).find(id => id !== restaurantId);
    const targetTableId = (reservation.resourceIds ?? []).find(id => id !== restaurantId);

    const [rows, setRows] = useState<DayRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [scope, setScope] = useState<Scope>('onwards');
    const mountedRef = useRef(false);

    const droppedTargetTableId = useMemo(
        () => (reservation.resourceIds ?? []).find(id => id !== restaurantId) ?? '',
        [reservation, restaurantId],
    );

    const linkedForDialog = useMemo(
        () => [...linked].sort((a, b) => {
            const aStart = a.expectedStartDate ? new Date(a.expectedStartDate).getTime() : 0;
            const bStart = b.expectedStartDate ? new Date(b.expectedStartDate).getTime() : 0;
            if (aStart !== bStart) return aStart - bStart;
            return String(a.meta?.eatPeriod ?? '').localeCompare(String(b.meta?.eatPeriod ?? ''));
        }),
        [linked],
    );

    // "Onwards" cutoff = start of *today*. We never want to show or update past
    // reservations regardless of what day is selected on the floorplan.
    const onwardsCutoffMs = useMemo(() => startOfDay(new Date()).getTime(), []);
    const originalReservationId = original.id ?? reservation.id;

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const tableOptions = useMemo(
        () => floorplanTables.map(t => ({id: t.id, name: t.resource.name ?? t.id})),
        [floorplanTables],
    );

    const visibleRows = useMemo(() => {
        if (scope === 'single') return rows.filter(r => r.reservation.id === originalReservationId);
        if (scope === 'onwards') return rows.filter(r => r.startDate.getTime() >= onwardsCutoffMs);
        return rows;
    }, [rows, scope, originalReservationId, onwardsCutoffMs]);

    // One entry per distinct eat period across the linked reservations, so the
    // header shows e.g. "Breakfast 08:00 – 09:30" + "Dinner 19:00 – 21:00".
    // We take the first occurrence of each period as representative.
    const periodSummary = useMemo(() => {
        const seen = new Map<string, {label: string; start?: Date; end?: Date}>();
        for (const r of linkedForDialog) {
            const key = String(r.meta?.eatPeriod ?? '').trim();
            if (!key || seen.has(key)) continue;
            seen.set(key, {
                label: getEatPeriodLabel(r.meta?.eatPeriod, t),
                start: r.expectedStartDate,
                end: r.expectedEndDate,
            });
        }
        return Array.from(seen.values()).sort((a, b) => {
            const aMs = a.start ? a.start.getTime() % 86_400_000 : 0;
            const bMs = b.start ? b.start.getTime() % 86_400_000 : 0;
            return aMs - bMs;
        });
    }, [linkedForDialog, t]);

    // Stay range from the dedicated accommodation fetch — independent of linked.
    const stayStart = accommodation?.expectedStartDay
        ? new Date(accommodation.expectedStartDay)
        : undefined;
    const stayEnd = accommodation?.expectedEndDay
        ? new Date(accommodation.expectedEndDay)
        : undefined;

    useEffect(() => {
        if (!open || linkedForDialog.length === 0) {
            if (open && linkedForDialog.length === 0) {
                setRows([]);
                setLoading(false);
            }
            return;
        }

        let cancelled = false;

        const fetchConflicts = async () => {
            setLoading(true);
            setError(null);

            try {
                const uniqueDays = Array.from(new Set(
                    linkedForDialog.map(r => r.expectedStartDay ?? '').filter(Boolean),
                ));

                const dayAssigned = new Map<string, FloorplanReservationItemDTO[]>();
                const fetchStatuses = [
                    ReservationStatus.CONFIRMED,
                    ReservationStatus.ACTIVE,
                    ReservationStatus.COMPLETED,
                    ReservationStatus.PENDING,
                ];

                await Promise.all(
                    uniqueDays.map(async (day) => {
                        const dayDate = new Date(day);
                        const assigned = await FloorplanService.getAssignedReservations({
                            restaurantId,
                            limit: {offset: 0, limit: 1000},
                            timespan: {
                                start: startOfDay(dayDate),
                                end: endOfDay(dayDate),
                            },
                            status: fetchStatuses,
                        });
                        dayAssigned.set(day, assigned);
                    }),
                );

                if (cancelled) return;

                const newRows: DayRow[] = linkedForDialog.map(res => {
                    const day = res.expectedStartDay ?? '';
                    const assignedForDay = dayAssigned.get(day) ?? [];
                    const currentTableId = (res.resourceIds ?? []).find(id => id !== restaurantId);

                    const conflicts = getOverlappingReservations(
                        droppedTargetTableId,
                        assignedForDay,
                        res.expectedStartDate,
                        res.expectedEndDate,
                        res.id,
                    );

                    return {
                        reservation: res,
                        date: day,
                        startDate: res.expectedStartDate!,
                        endDate: res.expectedEndDate!,
                        currentTableName: currentTableId
                            ? floorplanTables.find(ft => ft.id === currentTableId)?.resource.name ?? currentTableId
                            : '—',
                        targetTableId: droppedTargetTableId,
                        targetTableName: floorplanTables.find(ft => ft.id === droppedTargetTableId)?.resource.name ?? droppedTargetTableId,
                        hasConflict: conflicts.length > 0,
                        conflicts,
                        assignedForDay,
                    };
                });

                setRows(newRows);
            } catch (e) {
                console.error(e);
                if (!cancelled) setError(t('Failed to load conflict data'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        void fetchConflicts();
        return () => {
            cancelled = true;
        };
    }, [open, linkedForDialog, droppedTargetTableId, restaurantId, floorplanTables, t]);

    const handleTableChange = useCallback((reservationId: string, newTableId: string) => {
        setRows(prev => prev.map(row => {
            if (row.reservation.id !== reservationId) return row;

            const conflicts = getOverlappingReservations(
                newTableId,
                row.assignedForDay,
                row.startDate,
                row.endDate,
                row.reservation.id,
            );

            return {
                ...row,
                targetTableId: newTableId,
                targetTableName: floorplanTables.find(ft => ft.id === newTableId)?.resource.name ?? newTableId,
                hasConflict: conflicts.length > 0,
                conflicts,
            };
        }));
    }, [floorplanTables]);

    // `sendEmail` carries the staff's explicit intent: the split-button main action applies the
    // table changes silently, the menu item also sends the "reservation updated" guest email.
    const handleConfirm = useCallback(async (sendEmail: boolean) => {
        if (applying) {
            return;
        }

        const updates: DayUpdate[] = visibleRows.map(row => ({
            reservation: row.reservation,
            newTableId: row.targetTableId,
        }));

        setApplying(true);

        try {
            await onConfirm(updates, sendEmail);
        } finally {
            if (mountedRef.current) {
                setApplying(false);
            }
        }
    }, [applying, visibleRows, onConfirm]);

    const hasAnyConflict = visibleRows.some(r => r.hasConflict);

    return (
        <Dialog
            open={open}
            onClose={applying ? undefined : onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{sx: {maxHeight: '80vh'}}}
        >
            <DialogTitle>{t('Update linked reservations')}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {profiles && (
                        <Stack spacing={1}>
                            <RestaurantReservationPreviewCard
                                reservation={original}
                                profiles={profiles}
                                accommodationResources={accommodationResources}
                                sourceTableName={sourceTableId
                                    ? floorplanTables.find(ft => ft.id === sourceTableId)?.resource.name
                                    : undefined}
                                targetTableName={targetTableId
                                    ? floorplanTables.find(ft => ft.id === targetTableId)?.resource.name
                                    : undefined}
                                accommodationStayRange={stayStart && stayEnd
                                    ? `${formatFullDMY(stayStart)} – ${formatFullDMY(stayEnd)}`
                                    : undefined}
                            />

                            {/* One row per eat period: italic period name + that period's
                                typical timings. */}
                            {periodSummary.length > 0 && (
                                <Stack spacing={0.25} sx={{pl: 0.5}}>
                                    {periodSummary.map(period => (
                                        <Box
                                            key={period.label}
                                            sx={{display: "flex", alignItems: "baseline", gap: 1}}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontStyle="italic"
                                                sx={{minWidth: 90}}
                                            >
                                                {period.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDateHMRange(period.start, period.end)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    )}

                    {loading && (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5} py={4}>
                            <CircularProgress/>
                            <Typography variant="body2" color="text.secondary">
                                {t("Loading...")}
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Typography color="error" sx={{py: 2}}>{error}</Typography>
                    )}

                    {!loading && !error && linkedForDialog.length === 0 && (
                        <Typography color="text.secondary" sx={{py: 2}}>
                            {t('No linked reservations found')}
                        </Typography>
                    )}

                    {!loading && !error && rows.length > 0 && (
                        <Stack spacing={1.5}>
                            <ToggleButtonGroup
                                value={scope}
                                exclusive
                                onChange={(_, val) => {
                                    if (val) setScope(val);
                                }}
                                size="small"
                                disabled={applying}
                            >
                                <ToggleButton
                                    value="single">{t('This reservation only')}</ToggleButton>
                                <ToggleButton
                                    value="onwards">{t('This and all subsequent')}</ToggleButton>
                                {/*<ToggleButton*/}
                                {/*    value="all">{t('All linked reservations')}</ToggleButton>*/}
                            </ToggleButtonGroup>

                            {scope === 'single' && (
                                <Alert severity="info" variant="outlined">
                                    {t('Only this day\'s reservation will be updated. All other linked reservations remain unchanged.')}
                                </Alert>
                            )}
                            {scope === 'onwards' && (
                                <Alert severity="info" variant="outlined">
                                    {t('Today and all future days will be updated. Past days remain on their current table.')}
                                </Alert>
                            )}
                            {scope === 'all' && (
                                <Alert severity="info" variant="outlined">
                                    {t('All linked reservations will be updated, including past days.')}
                                </Alert>
                            )}

                            <Box sx={{display: 'flex', gap: 1.5, px: 1.5}}>
                                <Typography variant="body2" fontWeight={600}
                                            sx={{minWidth: 140, flexShrink: 0}}>
                                    {t('Date')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}
                                            sx={{minWidth: 90, flexShrink: 0}}>
                                    {t('Period')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}
                                            sx={{minWidth: 80, flexShrink: 0}}>
                                    {t('Current table')}
                                </Typography>
                                <Typography variant="body2"
                                            sx={{width: 14, flexShrink: 0}}/>
                                <Typography variant="body2" fontWeight={600}
                                            sx={{flex: 1, minWidth: 160}}>
                                    {t('New table')}
                                </Typography>
                                <Typography variant="body2"
                                            sx={{width: 20, flexShrink: 0}}/>
                            </Box>

                            {visibleRows.map((row) => {
                                const rowEatPeriodLabel = getEatPeriodLabel(row.reservation.meta?.eatPeriod, t);
                                // Already-started today's reservations are dimmed — they can't
                                // sensibly be moved onto another table at this point.
                                const hasStarted = row.startDate.getTime() <= Date.now();

                                return (
                                    <Box
                                        key={row.reservation.id ?? row.date}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: row.hasConflict ? '2px solid' : '1px solid',
                                            borderColor: row.hasConflict ? 'error.main' : 'divider',
                                            bgcolor: row.hasConflict ? 'error.50' : 'transparent',
                                            opacity: hasStarted ? 0.5 : 1,
                                        }}
                                    >
                                    <Box sx={{minWidth: 140, flexShrink: 0}}>
                                        <Typography variant="body2"
                                                    fontWeight={600}>
                                            {formatFullDMY(new Date(row.date))}
                                        </Typography>
                                        <Typography variant="body2"
                                                    color="text.secondary">
                                            {formatDateHMRange(row.startDate, row.endDate)}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{minWidth: 90, flexShrink: 0, pt: 0.25}}
                                        color="text.secondary"
                                    >
                                        {rowEatPeriodLabel || '—'}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{minWidth: 80, flexShrink: 0, pt: 0.25}}
                                        color="text.secondary"
                                    >
                                        {row.currentTableName}
                                    </Typography>

                                    <Typography variant="body2"
                                                color="text.secondary"
                                                sx={{pt: 0.25}}>
                                        →
                                    </Typography>

                                    <Box sx={{flex: 1, minWidth: 160}}>
                                        <Autocomplete
                                            size="small"
                                            disabled={applying}
                                            options={tableOptions.slice().sort((a, b) => {
                                                const aConflict = getOverlappingReservations(a.id, row.assignedForDay, row.startDate, row.endDate, row.reservation.id).length > 0;
                                                const bConflict = getOverlappingReservations(b.id, row.assignedForDay, row.startDate, row.endDate, row.reservation.id).length > 0;
                                                if (aConflict !== bConflict) return aConflict ? 1 : -1;
                                                return a.name.localeCompare(b.name, undefined, {numeric: true});
                                            })}
                                            getOptionLabel={o => o.name}
                                            value={tableOptions.find(o => o.id === row.targetTableId)}
                                            onChange={(_, val) => {
                                                if (val) handleTableChange(row.reservation.id!, val.id);
                                            }}
                                            renderOption={(props, option) => {
                                                const conflicts = getOverlappingReservations(
                                                    option.id,
                                                    row.assignedForDay,
                                                    row.startDate,
                                                    row.endDate,
                                                    row.reservation.id,
                                                );
                                                return (
                                                    <li {...props} key={option.id}>
                                                        <ConflictOptionContent
                                                            optionName={option.name}
                                                            conflict={conflicts[0]}
                                                            conflictsCount={conflicts.length}
                                                        />
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    placeholder={t('Select table')}
                                                />
                                            )}
                                            disableClearable
                                        />
                                        {row.hasConflict && (
                                            <Typography variant="body2"
                                                        color="error.main"
                                                        sx={{mt: 0.5}}>
                                                {t('Overlaps with')}: {getConflictReason(row.conflicts)}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{pt: 0.25}}>
                                        {row.hasConflict ? (
                                            <Warning color="error"
                                                     fontSize="small"/>
                                        ) : (
                                            <CheckCircle color="success"
                                                         fontSize="small"/>
                                        )}
                                    </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={applying}>{t('Cancel')}</Button>
                {/* The dropdown is the only way to notify the guests — a plain apply stays silent. */}
                <SaveWithEmailSplitButton
                    label={hasAnyConflict ? t('Apply with conflicts') : t('Apply')}
                    color={hasAnyConflict ? 'error' : 'primary'}
                    disabled={loading || applying || visibleRows.length === 0}
                    loading={applying}
                    onConfirm={(sendEmail) => void handleConfirm(sendEmail)}
                />
            </DialogActions>
        </Dialog>
    );
};

export default GroupReservationConflictDialog;
